import { observer } from 'mobx-react-lite'
import React from 'react'
import { Alert, Box, Divider, InputRadio, TextFieldWithLabel, Typography } from 'shared/ui'
import { LINK_CUSTOM_SHORT_URL } from 'shared/constants/links'
import { makeTextFieldAutoFocus, makeTextFieldElement } from 'shared/ui/TextField'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { type UrlShortenerModalStore } from 'pages/settings/pages/urlShortener/store/urlShortenerModalStore'
import {
  enumDomainOption,
  enumSubdomainOption,
} from 'pages/settings/pages/urlShortener/store/types'
import styles from './styles.module.scss'

export type IAddCustomDomainModalContentProps = {
  store: UrlShortenerModalStore
}

export const AddCustomDomainModalContent = observer(
  ({ store }: IAddCustomDomainModalContentProps) => {
    const {
      isDomainNameValid,
      domainName,
      domainOption,
      setDomainOption,
      setDomainName,
      fallbackUrl,
      setFallbackUrl,
      subdomainOption,
      setSubdomainOption,
      subdomainOptions,
      customSubdomainName,
      setCustomSubdomainName,
      errorsMap,
    } = store
    const fallbackUrlError = errorsMap.get('fallback_url_failed')
    const customSubdomainNameError = errorsMap.get('custom_subdomain_name')

    return (
      <Box marginTop={'-9px'}>
        <Alert
          marginBottom={23}
          item={{
            type: 'infoBlue',
            desc: 'Enter the domain or subdomain you want to use for branded short links.',
          }}
          rightActions={[
            {
              variant: 'link',
              type: 'primary',
              text: 'Learn more',
              link: LINK_CUSTOM_SHORT_URL,
            },
          ]}
        />

        <TextFieldWithLabel
          labelProps={{
            label: {
              text: 'Domain name',
            },
          }}
          textFieldProps={{
            value: domainName,
            onChange: setDomainName,
            onBlur: () => setDomainName(domainName.trim()),
            InputProps: {
              placeholder: 'example.yourdomain.com',
            },
            mainActions: [
              makeTextFieldAutoFocus({
                withFocus: true,
              }),
            ],
          }}
        />

        {isDomainNameValid && (
          <Box marginTop={'20px'}>
            <div className={styles.optionsWrap}>
              <InputRadio
                variant={'default'}
                checked={domainOption === enumDomainOption.links}
                radioSize='small'
                onChecked={() => setDomainOption(enumDomainOption.links)}
                label='This domain is dedicated to short links'
                className={styles.option}
              />
              <Divider style={{ borderColor: 'var(--border-neutral-default)' }} />
              <InputRadio
                variant={'default'}
                checked={domainOption === enumDomainOption.website}
                radioSize='small'
                onChecked={() => setDomainOption(enumDomainOption.website)}
                label='This domain is also used for my website or blog'
                className={styles.option}
              />
            </div>

            {domainOption === enumDomainOption.links && (
              <TextFieldWithLabel
                errorMessage={fallbackUrlError}
                labelProps={{
                  marginTop: 21,
                  questionTooltipProps: {
                    label: 'This backup link serves as a fallback if the primary short link fails',
                    type: 'description',
                    width: 240,
                  },
                  label: {
                    text: 'Fallback URL',
                    secondaryText: '(optional)',
                  },
                }}
                textFieldProps={{
                  value: fallbackUrl,
                  error: !!fallbackUrlError,
                  onChange: setFallbackUrl,
                  onBlur: () => setFallbackUrl(fallbackUrl.trim()),
                  InputProps: {
                    placeholder: 'https://yourdomain.com/landing',
                  },
                }}
              />
            )}

            {domainOption === enumDomainOption.website && (
              <Box marginTop={'20px'}>
                <SelectField
                  withSearch={false}
                  value={subdomainOption}
                  setValue={setSubdomainOption}
                  items={subdomainOptions}
                  labelProps={{
                    label: {
                      text: 'Enter a subdomain you’ll use for your links',
                    },
                  }}
                  btnProps={{
                    fullWidth: true,
                    typeBtn: 'stroke',
                    size: 'large',
                    style: {
                      height: '40px',
                      paddingRight: '8px',
                    },
                  }}
                  dropdownProps={{
                    ariaLabel: 'subdomain',
                  }}
                />

                {subdomainOption === enumSubdomainOption.custom && (
                  <TextFieldWithLabel
                    errorBorder={!!customSubdomainNameError}
                    errorMessage={customSubdomainNameError}
                    marginTop={'8px'}
                    textFieldProps={{
                      value: customSubdomainName,
                      onChange: setCustomSubdomainName,
                      onBlur: () => setCustomSubdomainName(customSubdomainName.trim()),
                      mainActions: [
                        makeTextFieldAutoFocus({
                          withFocus: true,
                        }),
                      ],
                    }}
                    rightActions={[
                      makeTextFieldElement({
                        element: (
                          <Typography
                            tag='span'
                            variant={'text-input-field-r'}
                            color={'var(--content-primary-tertiary)'}
                            ariaLabel={'domainName'}
                          >
                            .{domainName}
                          </Typography>
                        ),
                      }),
                    ]}
                  />
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    )
  }
)
